import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { FiInfo } from "react-icons/fi";

interface PieChartProps {
  title: string;
  data: number[];
  labels: string[];
  colors?: string[];
  tooltipText?: string;
}

const Tooltip = ({ text }: { text: string }) => (
  <div className="group relative inline-block">
    <button className="flex items-center justify-center rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800">
      <FiInfo className="text-gray-500 dark:text-gray-400" />
    </button>
    <div className="pointer-events-none absolute bottom-full right-0 mb-2 hidden w-48 rounded bg-gray-800 p-2 text-center text-xs text-white opacity-0 transition-opacity group-hover:block group-hover:opacity-100 z-50">
      {text}
      <div className="absolute right-3 top-full -ml-1 h-0 w-0 border-4 border-solid border-transparent border-t-gray-800"></div>
    </div>
  </div>
);

export default function PieChart({
  title,
  data,
  labels,
  colors,
  tooltipText,
}: PieChartProps) {
  const defaultColors = [
    "#a855f7", // purple-500
    "#3b82f6", // blue-500
    "#10b981", // green-500
    "#f59e0b", // amber-500
    "#ef4444", // red-500
    "#ec4899", // pink-500
    "#8b5cf6", // violet-500
  ];

  const options: ApexOptions = {
    colors: colors || defaultColors,
    chart: {
      fontFamily: "Outfit, sans-serif",
      type: "donut",
    },
    labels: labels,
    legend: {
      show: true,
      position: "bottom",
      fontFamily: "Outfit",
      fontSize: "14px",
      fontWeight: 600,
      markers: {
        size: 8,
        shape: "circle",
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          background: "transparent",
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: "16px",
              fontWeight: 600,
              offsetY: -10,
            },
            value: {
              show: true,
              fontSize: "24px",
              fontWeight: 700,
              offsetY: 10,
              formatter: (val: string) => val,
            },
            total: {
              show: true,
              label: "Total",
              fontSize: "14px",
              fontWeight: 600,
              formatter: (w: { globals: { seriesTotals: number[] } }) => {
                const total = w.globals.seriesTotals.reduce(
                  (a: number, b: number) => a + b,
                  0,
                );
                return total.toString();
              },
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 3,
      colors: ["#ffffff"],
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val}`,
      },
      style: {
        fontSize: "14px",
        fontFamily: "Outfit",
      },
    },
  };

  const series = data;

  return (
    <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 px-5 pb-5 pt-5 shadow-sm transition-all duration-300 hover:shadow-lg dark:border-gray-800 dark:from-white/[0.03] dark:to-white/[0.01] sm:px-6 sm:pt-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-lg font-bold text-transparent dark:from-purple-400 dark:to-pink-400">
          {title}
        </h3>
        {tooltipText && (
          <div className="relative inline-block text-left">
            <Tooltip text={tooltipText} />
          </div>
        )}
      </div>

      <div className="flex justify-center">
        <Chart
          options={options}
          series={series}
          type="donut"
          height={320}
          width={350}
        />
      </div>
    </div>
  );
}
